import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  RefreshCw, AlertTriangle, Check, Clock, 
  Loader2, ArrowLeft, DollarSign, CreditCard, Shield
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { API, getAuthHeaders } from '../utils/api';

// PayPal SVG Icon
const PayPalIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.64h6.673c2.206 0 3.97.667 5.246 1.984 1.242 1.28 1.792 2.96 1.636 5.001-.16 2.098-.955 3.799-2.363 5.058-1.364 1.22-3.128 1.84-5.244 1.84H9.07a.77.77 0 0 0-.757.64l-.943 5.736a.641.641 0 0 1-.633.538h-1.66z"/>
  </svg>
);

export default function Refunds() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [refundRequests, setRefundRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cardLast4, setCardLast4] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, refundsRes] = await Promise.all([
        axios.get(`${API}/payments/history`, { withCredentials: true, headers: getAuthHeaders() }),
        axios.get(`${API}/refunds`, { withCredentials: true, headers: getAuthHeaders() })
      ]);
      
      setPayments(paymentsRes.data.payments || []);
      setRefundRequests(refundsRes.data.refunds || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefundRequest = (payment) => {
    setSelectedPayment(payment);
    setRefundReason('');
    setCardLast4('');
    setDialogOpen(true);
  };

  const submitRefundRequest = async () => {
    if (!refundReason.trim()) {
      toast.error('Please provide a reason for the refund');
      return;
    }
    
    if (!cardLast4 || cardLast4.length !== 4 || !/^\d{4}$/.test(cardLast4)) {
      toast.error('Please enter the last 4 digits of your card');
      return;
    }
    
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/refunds/request-secure`, {
        transaction_id: selectedPayment.transaction_id,
        payment_id: selectedPayment.payment_id,
        reason: refundReason,
        amount: selectedPayment.amount,
        card_last4: cardLast4
      }, { withCredentials: true, headers: getAuthHeaders() });
      
      toast.success('Refund request submitted! We will process it within 3-5 business days.');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit refund request');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
      case 'completed':
        return <Badge className="bg-emerald-500/20 text-emerald-400">Refunded</Badge>;
      case 'pending':
        return <Badge className="bg-amber-500/20 text-amber-400">Pending Review</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-400">Rejected</Badge>;
      default:
        return <Badge className="bg-slate-500/20 text-slate-400">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto" />
          <p className="mt-4 text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Filter for PayPal payments only
  const eligiblePayments = payments.filter(p => 
    p.payment_status === 'paid' && 
    p.payment_method === 'paypal' &&
    !refundRequests.some(r => r.transaction_id === p.transaction_id)
  );

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="refunds-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/account')}
          className="text-slate-400"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Account
        </Button>
      </div>

      <div>
        <h1 className="text-4xl font-bold font-['Manrope'] mb-2">Refunds</h1>
        <p className="text-slate-400">Request a refund for your PayPal payments</p>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-500/10 border-blue-500/30 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-400 mt-0.5" />
          <div>
            <p className="text-blue-400 font-medium">Refund Policy</p>
            <p className="text-sm text-blue-300/80 mt-1">
              Refunds are available within 30 days of purchase. Once approved, PayPal will process 
              the refund to your original payment method within 3-5 business days. Your subscription 
              will be cancelled upon refund approval.
            </p>
          </div>
        </div>
      </Card>

      {/* Eligible Payments */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Eligible for Refund</h2>
        {eligiblePayments.length > 0 ? (
          <div className="space-y-3">
            {eligiblePayments.map((payment) => (
              <Card 
                key={payment.transaction_id}
                className="bg-[#121214] border-zinc-800 p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#0070ba]/20 rounded-full flex items-center justify-center">
                      <PayPalIcon className="w-5 h-5 text-[#0070ba]" />
                    </div>
                    <div>
                      <p className="font-medium capitalize">
                        {payment.plan_id?.replace('_', ' ')}
                      </p>
                      <p className="text-sm text-slate-400">
                        {new Date(payment.created_at).toLocaleDateString()} • PayPal
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="font-semibold text-lg">${payment.amount}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRefundRequest(payment)}
                      className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                      data-testid={`refund-btn-${payment.transaction_id}`}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Request Refund
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-[#121214] border-zinc-800 p-8 text-center">
            <DollarSign className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No payments eligible for refund</p>
            <p className="text-sm text-slate-500 mt-1">
              Only PayPal payments within 30 days are eligible
            </p>
          </Card>
        )}
      </div>

      {/* Refund History */}
      {refundRequests.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Refund History</h2>
          <div className="space-y-3">
            {refundRequests.map((refund) => (
              <Card 
                key={refund.refund_id}
                className="bg-[#121214] border-zinc-800 p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      refund.status === 'completed' || refund.status === 'approved' ? 'bg-emerald-500/20' :
                      refund.status === 'pending' ? 'bg-amber-500/20' :
                      'bg-red-500/20'
                    }`}>
                      {refund.status === 'completed' || refund.status === 'approved' ? (
                        <Check className="w-5 h-5 text-emerald-400" />
                      ) : refund.status === 'pending' ? (
                        <Clock className="w-5 h-5 text-amber-400" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">Refund Request</p>
                      <p className="text-sm text-slate-400">
                        {new Date(refund.created_at).toLocaleDateString()}
                      </p>
                      {refund.reason && (
                        <p className="text-xs text-slate-500 mt-1 italic">
                          "{refund.reason}"
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="font-semibold">${refund.amount}</p>
                    {getStatusBadge(refund.status)}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Refund Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121214] border-zinc-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-400" />
              Request Refund
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              You're requesting a refund for your {selectedPayment?.plan_id?.replace('_', ' ')} subscription (${selectedPayment?.amount}).
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Card Verification Section */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-400">Security Verification</p>
                  <p className="text-xs text-slate-400 mt-1">
                    For your protection, please enter the last 4 digits of the card used for this payment.
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <Label className="text-sm text-slate-400 mb-2 block flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Last 4 digits of your card *
              </Label>
              <Input
                type="text"
                maxLength={4}
                value={cardLast4}
                onChange={(e) => setCardLast4(e.target.value.replace(/\D/g, ''))}
                placeholder="1234"
                className="bg-zinc-900 border-zinc-800 text-center text-lg tracking-widest font-mono w-32"
                data-testid="card-last4-input"
              />
            </div>
            
            <div>
              <Label className="text-sm text-slate-400 mb-2 block">
                Reason for refund *
              </Label>
              <Textarea
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="Please tell us why you'd like a refund..."
                className="bg-zinc-900 border-zinc-800 min-h-[100px]"
              />
              <p className="text-xs text-slate-500 mt-2">
                Your refund will be processed via PayPal within 3-5 business days after approval.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={submitRefundRequest}
              disabled={processing || !refundReason.trim() || cardLast4.length !== 4}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="submit-refund-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
